import { localStorage } from '@tunein/web-utils';
import { apiBeta } from 'src/client/api';
import { player } from 'src/client/player';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { stores } from 'src/client/stores';
import config from 'src/common/config';
import { hidePopoutPlayerDialogCookie } from 'src/common/constants/cookies';
import {
  OPEN_IN_EMBED_DIALOG_FREQUENCY,
  OPEN_IN_EMBED_ENABLED,
} from 'src/common/constants/experiments/popout';
import { PREROLL_PLAYED } from 'src/common/constants/player/preroll';
import { selectIsDiscord } from 'src/common/selectors/app';
import { selectExperiment } from 'src/common/selectors/config';
import { getCookie, setCookie } from 'src/common/utils/cookie';
import openStandardWindow from 'src/common/utils/window/openStandardWindow';
import { POPUP_PLAYER_READY, UPDATE_POPUP_PLAYER } from './constants/message';

const { insecureEmbedOrigin } = config;

const externalUrlPopOutDimensions = {
  width: 1180,
  height: 900,
};
const standardPopoutDimensions = {
  width: 640,
  height: 100,
};

export class PopoutPlayer {
  #popoutWindow = null;
  #guideId = null;
  #isPremium = null;
  #partnerId = '';

  get isEnabled() {
    const state = storeSingleton.state;
    const openInEmbedEnabled = selectExperiment(state, OPEN_IN_EMBED_ENABLED);
    const isDiscord = selectIsDiscord(state);

    return openInEmbedEnabled && !isDiscord;
  }

  // TODO: delete after Player Manager migration is complete
  get isOpen() {
    return this.#isOpen;
  }

  init() {
    player.on('error', (event) => {
      if (
        popoutPlayer.isEnabled &&
        event.error?.fatal &&
        event.isLastSource &&
        event.hadOnlyHttpStreams
      ) {
        popoutPlayer.promptUser();
      }
    });

    player.on('playing', () => {
      if (this.#isOpen) {
        this.#close();
      }
    });
  }

  promptUser() {
    const { setPopoutState } = stores.playerStore.getState();

    if (getCookie(hidePopoutPlayerDialogCookie.name)) {
      setPopoutState({ showButton: true, isDialogOpen: false });
      return;
    }

    const frequency = apiBeta.getConfigSetting(OPEN_IN_EMBED_DIALOG_FREQUENCY, 1);

    setCookie(hidePopoutPlayerDialogCookie.name, frequency);

    setPopoutState({ showButton: true, isDialogOpen: true });
  }

  // TODO: delete legacyOptions after Player Manager migration is complete
  openOrUpdate(legacyOptions = {}) {
    const previousGuideId = this.#guideId;

    if (!player.isActive) {
      this.#guideId = legacyOptions.guideId;
      this.#isPremium = legacyOptions.isPremium;
      this.#partnerId = legacyOptions.partnerId;
    } else {
      const { currentGuideItem } = stores.playerStore.getState();

      this.#guideId = currentGuideItem.playableGuideId;
      this.#isPremium = apiBeta.isPremium;
      this.#partnerId = apiBeta.partnerId;

      if (player.streamData?.externalPlayerUrl) {
        openStandardWindow({
          url: player.streamData.externalPlayerUrl,
          ...externalUrlPopOutDimensions,
        });
        return;
      }
    }

    this.#isOpen ? this.#send(previousGuideId) : this.#open();
  }

  // TODO: delete after Player Manager migration is complete
  close() {
    this.#close();
  }

  get #isOpen() {
    return Boolean(this.#popoutWindow && !this.#popoutWindow.closed);
  }

  get #embedUrl() {
    return `${insecureEmbedOrigin}/embed/player/${this.#guideId}?partnerId=${this.#partnerId}`;
  }

  #focus() {
    this.#popoutWindow?.focus();
  }

  #handlePopoutPremiumStatusEvent = (event) => {
    const { data, origin } = event;

    const type = data?.type;
    const isValidMessage = type === POPUP_PLAYER_READY && origin === insecureEmbedOrigin;

    if (isValidMessage) {
      this.#send();
    }
  };

  #openWindow({ width, height }) {
    const { screenLeft, screenX, innerWidth } = window;

    const attributeEntries = Object.entries({
      width,
      height,
      top: 0,
      left: (screenLeft || screenX) + (innerWidth - width),
    });
    const attributes = attributeEntries.map((entry) => entry.join('=')).join(',');

    return window.open(this.#embedUrl, '_blank', attributes);
  }

  #open() {
    // this is used as a way to communicate the premium status once the popout window loads
    window.addEventListener('message', this.#handlePopoutPremiumStatusEvent);

    this.#popoutWindow = this.#openWindow(standardPopoutDimensions);
  }

  #send(previousGuideId) {
    this.#focus();

    if (previousGuideId && previousGuideId !== this.#guideId) {
      this.#popoutWindow?.location.replace(this.#embedUrl);
      return;
    }

    const payload = {
      type: UPDATE_POPUP_PLAYER,
      isPremium: this.#isPremium,
      prerollPlayed: player.isActive
        ? player.didPlayPreroll
        : JSON.parse(localStorage.get(PREROLL_PLAYED)) === true,
    };

    this.#popoutWindow?.postMessage(payload, insecureEmbedOrigin);
  }

  #close() {
    window.removeEventListener('message', this.#handlePopoutPremiumStatusEvent);
    this.#popoutWindow?.close();
  }
}

export const popoutPlayer = new PopoutPlayer();
